// src/components/TicketViewsParameters.js
// Ticket views — each saved view becomes a tab on the dashboard's ticket
// table (next to "General", /dashboard?activeTab=view:<id>). The page is
// designed around that fact: a tab-bar preview in dashboard order, rows
// summarizing each view's filters with their real semantics (exclusions vs
// restrictions; empty restriction = everything), and a sectioned modal.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaPlus, FaPen, FaTrash, FaTimes, FaBan, FaTag, FaEnvelope, FaUserTie,
  FaCheckCircle, FaTimesCircle, FaSpinner, FaExternalLinkAlt, FaColumns
} from 'react-icons/fa';
import { getStatusLabel } from '../constants/ticketStatuses';
import './TicketViewsParameters.css';

const createEmptyFormState = () => ({
  name: '',
  description: '',
  statusesToExclude: [],
  resolutionActorsToExclude: [],
  topics: [],
  channelIds: [],
  employeeIds: []
});

const TicketViewsParameters = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [views, setViews] = useState(null); // null = loading
  const [submitting, setSubmitting] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);
  const [feedback, setFeedback] = useState(null); // {type, text}

  const [showModal, setShowModal] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [formState, setFormState] = useState(() => createEmptyFormState());
  const [modalError, setModalError] = useState(null);

  const [options, setOptions] = useState(null); // null = loading
  const feedbackTimer = useRef(null);

  const showFeedback = useCallback((type, text) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, text });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
  }, []);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const fetchTicketViews = useCallback(async () => {
    if (!localStorage.getItem('access_token')) { navigate('/'); return; }
    try {
      const response = await axios.get('/api/tickets/ticket-views', { headers: authHeaders() });
      setViews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      setViews([]);
      showFeedback('error', err.response?.data?.detail || t('ticketViewsParameters.fetchError', 'Échec du chargement des vues de tickets.'));
    }
  }, [navigate, t, showFeedback]);

  const fetchTicketViewOptions = useCallback(async () => {
    try {
      const response = await axios.get('/api/tickets/ticket-views/options', { headers: authHeaders() });
      const data = response.data || {};
      setOptions({
        statuses: Array.isArray(data.statuses) ? data.statuses : [],
        resolutionActors: Array.isArray(data.resolution_actors) ? data.resolution_actors : [],
        topics: Array.isArray(data.topics) ? data.topics : [],
        channels: Array.isArray(data.channels) ? data.channels : [],
        employees: Array.isArray(data.employees) ? data.employees : []
      });
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      setOptions({ statuses: [], resolutionActors: [], topics: [], channels: [], employees: [] });
    }
  }, [navigate]);

  useEffect(() => {
    fetchTicketViews();
    fetchTicketViewOptions();
  }, [fetchTicketViews, fetchTicketViewOptions]);

  // Dashboard shows tabs oldest-first (backend sorts created_at asc) —
  // the list mirrors that order so it matches the tab bar users see.
  const orderedViews = useMemo(() => {
    return [...(views || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [views]);

  const getViewId = (view) => view.id || view._id;

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString(i18n.language || 'fr', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  // ------------------------------------------------------------ modal logic

  const openCreateModal = () => {
    setEditingView(null);
    setFormState(createEmptyFormState());
    setModalError(null);
    setShowModal(true);
  };

  const openEditModal = (view) => {
    const filters = view.filters || {};
    setEditingView(view);
    setFormState({
      name: view.name || '',
      description: view.description || '',
      statusesToExclude: (filters.statuses_to_exclude || []).map(String),
      resolutionActorsToExclude: (filters.resolution_actors_to_exclude || []).map(String),
      topics: (filters.topics || []).map(String),
      channelIds: (filters.channels || filters.channel_ids || []).map((c) => String(c?.id || c?._id || c)),
      employeeIds: (filters.employees || filters.employee_ids || []).map((e) => String(e?.id || e?._id || e))
    });
    setModalError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingView(null);
    setFormState(createEmptyFormState());
    setModalError(null);
  };

  const toggleSelection = (field, rawValue) => {
    const val = rawValue?.toString();
    if (!val) return;
    setFormState((prev) => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(val) ? current.filter((i) => i !== val) : [...current, val]
      };
    });
  };

  const handleSave = async () => {
    const name = formState.name.trim();
    if (!name) {
      setModalError(t('ticketViewsParameters.nameRequired', 'Le nom est requis.'));
      return;
    }
    setSubmitting(true);
    setModalError(null);
    const payload = {
      name,
      description: formState.description.trim() || null,
      filters: {
        statuses_to_exclude: formState.statusesToExclude,
        resolution_actors_to_exclude: formState.resolutionActorsToExclude,
        topics: formState.topics,
        channel_ids: formState.channelIds,
        employee_ids: formState.employeeIds
      }
    };

    try {
      if (editingView) {
        await axios.put(`/api/tickets/ticket-views/${getViewId(editingView)}`, payload, { headers: authHeaders() });
        showFeedback('success', t('ticketViewsParameters.updateSuccess', 'Vue mise à jour.'));
      } else {
        await axios.post('/api/tickets/ticket-views', payload, { headers: authHeaders() });
        showFeedback('success', t('ticketViewsParameters.createSuccess', 'Vue créée — elle apparaît maintenant comme onglet du tableau de bord.'));
      }
      closeModal();
      fetchTicketViews();
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      setModalError(
        err.response?.status === 409
          ? t('ticketViewsParameters.duplicateName', 'Une vue porte déjà ce nom.')
          : err.response?.data?.detail || t('ticketViewsParameters.createError', 'Échec de l’enregistrement de la vue.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (view) => {
    const viewId = getViewId(view);
    const shouldDelete = window.confirm(
      t('ticketViewsParameters.deleteConfirm', 'Supprimer cette vue ? L’onglet correspondant disparaîtra du tableau de bord.')
    );
    if (!shouldDelete) return;
    setDeletingIds((prev) => [...prev, viewId]);
    try {
      await axios.delete(`/api/tickets/ticket-views/${viewId}`, { headers: authHeaders() });
      setViews((prev) => (prev || []).filter((v) => getViewId(v) !== viewId));
      showFeedback('success', t('ticketViewsParameters.deleteSuccess', 'Vue supprimée.'));
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      showFeedback('error', err.response?.data?.detail || t('ticketViewsParameters.deleteError', 'Échec de la suppression de la vue.'));
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== viewId));
    }
  };

  const openOnDashboard = (view) => {
    navigate(`/dashboard?activeTab=view:${getViewId(view)}`);
  };

  // ------------------------------------------------------------ summaries

  const getChannelName = (ref) => {
    if (typeof ref === 'object' && ref?.name) return ref.name;
    const ch = (options?.channels || []).find((c) => String(c.id || c._id) === String(ref));
    return ch?.name || t('ticketViewsParameters.unknownChannel', 'Canal inconnu');
  };

  const getEmployeeName = (ref) => {
    if (typeof ref === 'object' && ref?.name) return ref.name;
    const emp = (options?.employees || []).find((e) => String(e.id || e._id) === String(ref));
    return emp?.name || t('ticketViewsParameters.unknownEmployee', 'Employé inconnu');
  };

  // One segment per filter family: exclusions marked, restrictions plain.
  const filterSegments = (view) => {
    const filters = view.filters || {};
    const segments = [];
    const excludedStatuses = filters.statuses_to_exclude || [];
    const excludedActors = filters.resolution_actors_to_exclude || [];
    const topics = filters.topics || [];
    const channels = filters.channels || filters.channel_ids || [];
    const employees = filters.employees || filters.employee_ids || [];

    if (excludedStatuses.length > 0) {
      segments.push({
        key: 'statuses', exclude: true, icon: <FaBan />,
        label: t('ticketViewsParameters.segHiddenStatuses', 'Masque'),
        value: excludedStatuses.map((s) => getStatusLabel(t, s)).join(', ')
      });
    }
    if (excludedActors.length > 0) {
      segments.push({
        key: 'actors', exclude: true, icon: <FaBan />,
        label: t('ticketViewsParameters.segHiddenActors', 'Masque résolutions'),
        value: excludedActors.map((a) => t(`resolutionActors.${a}`, a)).join(', ')
      });
    }
    if (topics.length > 0) {
      segments.push({
        key: 'topics', exclude: false, icon: <FaTag />,
        label: t('ticketViewsParameters.segTopics', 'Sujets'),
        value: topics.join(', ')
      });
    }
    if (channels.length > 0) {
      segments.push({
        key: 'channels', exclude: false, icon: <FaEnvelope />,
        label: t('ticketViewsParameters.segChannels', 'Canaux'),
        value: channels.map(getChannelName).join(', ')
      });
    }
    if (employees.length > 0) {
      segments.push({
        key: 'employees', exclude: false, icon: <FaUserTie />,
        label: t('ticketViewsParameters.segEmployees', 'Employés'),
        value: employees.map(getEmployeeName).join(', ')
      });
    }
    return segments;
  };

  // ------------------------------------------------------------ modal fields

  const chipField = (label, field, items, getValue, getLabel, getColor) => {
    const selected = formState[field] || [];
    return (
      <div className="tvp-field">
        <div className="tvp-field-head">
          <label>{label}</label>
          {selected.length > 0 && (
            <button
              type="button"
              className="tvp-field-clear"
              onClick={() => setFormState((prev) => ({ ...prev, [field]: [] }))}
            >
              {t('ticketViewsParameters.clearSelection', 'Effacer la sélection')} ({selected.length})
            </button>
          )}
        </div>
        {options === null ? (
          <div className="tvp-field-empty">{t('ticketViewsParameters.loadingOptions', 'Chargement des options...')}</div>
        ) : items.length === 0 ? (
          <div className="tvp-field-empty">{t('ticketViewsParameters.noOptionsAvailable', 'Aucune option disponible')}</div>
        ) : (
          <div className="tvp-chip-grid">
            {items.map((item) => {
              const value = getValue(item);
              if (!value) return null;
              const isOn = selected.includes(value);
              const color = getColor ? getColor(item) : null;
              return (
                <button
                  key={`${field}-${value}`}
                  type="button"
                  className={`tvp-chip ${isOn ? 'on' : ''}`}
                  aria-pressed={isOn}
                  onClick={() => toggleSelection(field, value)}
                >
                  {color && <span className="tvp-color-dot" style={{ backgroundColor: color }} />}
                  {getLabel(item)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ------------------------------------------------------------ rendering

  const loading = views === null;
  const list = orderedViews;

  return (
    <div className="ticket-views-page">
      <div className="tvp-card">
        {/* Header */}
        <div className="tvp-card-header">
          <div className="tvp-header-left">
            <h1>{t('ticketViewsParameters.title', 'Vues de tickets')}</h1>
            <p className="tvp-subtitle">
              {t('ticketViewsParameters.subtitle', 'Chaque vue devient un onglet du tableau de bord et filtre la liste des tickets.')}
            </p>
          </div>
          <div className="tvp-header-right">
            {!loading && (
              <span className="tvp-count">
                {t('ticketViewsParameters.countLabel', '{{total}} vues', { total: list.length })}
              </span>
            )}
            <button type="button" className="u-btn u-btn--primary" onClick={openCreateModal}>
              <FaPlus /> {t('ticketViewsParameters.create', 'Créer une vue')}
            </button>
          </div>
        </div>

        {feedback && (
          <div className={`tvp-feedback ${feedback.type}`} role="status">
            {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            {feedback.text}
          </div>
        )}

        {loading && (
          <div className="tvp-skeleton" aria-hidden="true">
            {Array.from({ length: 4 }, (_, i) => <div key={i} className="tvp-skeleton-row" />)}
          </div>
        )}

        {!loading && list.length === 0 && (
          <div className="tvp-empty">
            <FaColumns className="tvp-empty-icon" />
            <p>{t('ticketViewsParameters.emptyDesc', 'Aucune vue pour le moment. Créez-en une : elle apparaîtra comme onglet du tableau de bord, avec ses propres filtres.')}</p>
          </div>
        )}

        {!loading && list.length > 0 && (
          <>
            {/* Dashboard tab-bar preview, in real tab order */}
            <div className="tvp-preview">
              <span className="tvp-preview-label">
                {t('ticketViewsParameters.previewLabel', 'Aperçu des onglets du tableau de bord')}
              </span>
              <div className="tvp-preview-tabs">
                {list.map((view) => (
                  <button
                    key={getViewId(view)}
                    type="button"
                    className="tvp-preview-tab"
                    title={t('ticketViewsParameters.openOnDashboard', 'Ouvrir sur le tableau de bord')}
                    onClick={() => openOnDashboard(view)}
                  >
                    {view.name}
                  </button>
                ))}
                <span className="tvp-preview-tab tvp-preview-tab-general">
                  {t('general', 'Général')}
                </span>
              </div>
            </div>

            <ul className="tvp-list">
              {list.map((view) => {
                const viewId = getViewId(view);
                const segments = filterSegments(view);
                const deleting = deletingIds.includes(viewId);
                const created = formatDate(view.created_at);
                const updated = formatDate(view.updated_at);
                return (
                  <li key={viewId} className="tvp-row">
                    <div className="tvp-row-main">
                      <div className="tvp-row-title">
                        <span className="tvp-row-name">{view.name}</span>
                        {view.description && (
                          <span className="tvp-row-desc" title={view.description}>{view.description}</span>
                        )}
                      </div>
                      <div className="tvp-row-filters">
                        {segments.length === 0 ? (
                          <span className="tvp-seg tvp-seg-none">
                            {t('ticketViewsParameters.noFilters', 'Aucun filtre — tous les tickets')}
                          </span>
                        ) : (
                          segments.map((seg) => (
                            <span key={seg.key} className={`tvp-seg ${seg.exclude ? 'tvp-seg-exclude' : ''}`} title={`${seg.label} : ${seg.value}`}>
                              {seg.icon}
                              <b>{seg.label}&nbsp;:</b> {seg.value}
                            </span>
                          ))
                        )}
                      </div>
                      {(created || updated) && (
                        <div className="tvp-row-meta">
                          {created && t('ticketViewsParameters.createdOn', 'Créée le {{date}}', { date: created })}
                          {created && updated && updated !== created && ' · '}
                          {updated && updated !== created && t('ticketViewsParameters.updatedOn', 'modifiée le {{date}}', { date: updated })}
                        </div>
                      )}
                    </div>
                    <div className="tvp-row-actions">
                      <button
                        type="button"
                        className="tvp-icon-btn"
                        onClick={() => openOnDashboard(view)}
                        title={t('ticketViewsParameters.openOnDashboard', 'Ouvrir sur le tableau de bord')}
                        aria-label={t('ticketViewsParameters.openOnDashboard', 'Ouvrir sur le tableau de bord')}
                      >
                        <FaExternalLinkAlt />
                      </button>
                      <button
                        type="button"
                        className="tvp-icon-btn"
                        onClick={() => openEditModal(view)}
                        title={t('ticketViewsParameters.edit', 'Modifier')}
                        aria-label={t('ticketViewsParameters.edit', 'Modifier')}
                      >
                        <FaPen />
                      </button>
                      <button
                        type="button"
                        className="tvp-icon-btn danger"
                        onClick={() => handleDelete(view)}
                        disabled={deleting}
                        title={t('ticketViewsParameters.delete', 'Supprimer')}
                        aria-label={t('ticketViewsParameters.delete', 'Supprimer')}
                      >
                        {deleting ? <FaSpinner className="tvp-spin" /> : <FaTrash />}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Create / edit modal */}
      {showModal && (
        <div className="tvp-modal-backdrop" onClick={closeModal}>
          <div className="tvp-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="tvp-modal-header">
              <h3>
                {editingView
                  ? t('ticketViewsParameters.editTitle', 'Modifier la vue')
                  : t('ticketViewsParameters.createTitle', 'Créer une vue')}
              </h3>
              <button type="button" className="tvp-modal-close" onClick={closeModal} aria-label={t('common.cancel', 'Annuler')}>
                <FaTimes />
              </button>
            </div>
            <div className="tvp-modal-body">
              {/* Identity */}
              <div className="tvp-form-section">
                <h4>{t('ticketViewsParameters.sectionIdentity', 'Identité')}</h4>
                <div className="tvp-field">
                  <div className="tvp-field-head">
                    <label htmlFor="tvp-name">{t('ticketViewsParameters.nameLabel', 'Nom')} *</label>
                  </div>
                  <input
                    id="tvp-name"
                    type="text"
                    value={formState.name}
                    onChange={(e) => setFormState((f) => ({ ...f, name: e.target.value }))}
                    placeholder={t('ticketViewsParameters.namePlaceholder', 'Ex. : SAV urgent, Tickets Gmail…')}
                    autoFocus
                  />
                </div>
                <div className="tvp-field">
                  <div className="tvp-field-head">
                    <label htmlFor="tvp-desc">{t('ticketViewsParameters.descriptionLabel', 'Description')}</label>
                  </div>
                  <textarea
                    id="tvp-desc"
                    rows={2}
                    value={formState.description}
                    onChange={(e) => setFormState((f) => ({ ...f, description: e.target.value }))}
                    placeholder={t('ticketViewsParameters.descriptionPlaceholder', 'Description optionnelle')}
                  />
                </div>
              </div>

              {/* Exclusions */}
              <div className="tvp-form-section">
                <h4>{t('ticketViewsParameters.sectionHide', 'Masquer')}</h4>
                <p className="tvp-section-hint">
                  {t('ticketViewsParameters.sectionHideHint', 'Les tickets correspondants n’apparaîtront pas dans cette vue.')}
                </p>
                {chipField(
                  t('ticketViewsParameters.statusesLabel', 'Statuts à masquer'),
                  'statusesToExclude',
                  options?.statuses || [],
                  (s) => s?.toString() || '',
                  (s) => getStatusLabel(t, s)
                )}
                {chipField(
                  t('ticketViewsParameters.resolutionActorsLabel', 'Résolutions à masquer'),
                  'resolutionActorsToExclude',
                  options?.resolutionActors || [],
                  (a) => a?.toString() || '',
                  (a) => t(`resolutionActors.${a}`, a)
                )}
              </div>

              {/* Restrictions */}
              <div className="tvp-form-section">
                <h4>{t('ticketViewsParameters.sectionLimit', 'Limiter à')}</h4>
                <p className="tvp-section-hint">
                  {t('ticketViewsParameters.sectionLimitHint', 'Aucune sélection = tout est affiché.')}
                </p>
                {chipField(
                  t('ticketViewsParameters.topicsLabel', 'Sujets'),
                  'topics',
                  options?.topics || [],
                  (tp) => tp?.toString() || '',
                  (tp) => tp
                )}
                {chipField(
                  t('ticketViewsParameters.channelsLabel', 'Canaux'),
                  'channelIds',
                  options?.channels || [],
                  (c) => c?.id?.toString() || c?._id?.toString() || '',
                  (c) => c?.name || t('ticketViewsParameters.unknownChannel', 'Canal inconnu'),
                  (c) => c?.color || null
                )}
                {chipField(
                  t('ticketViewsParameters.employeesLabel', 'Employés'),
                  'employeeIds',
                  options?.employees || [],
                  (e) => e?.id?.toString() || e?._id?.toString() || '',
                  (e) => e?.name || t('ticketViewsParameters.unknownEmployee', 'Employé inconnu')
                )}
              </div>

              {modalError && <div className="tvp-modal-error">{modalError}</div>}
            </div>
            <div className="tvp-modal-footer">
              <button type="button" className="u-btn u-btn--secondary" onClick={closeModal} disabled={submitting}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={handleSave} disabled={submitting}>
                {submitting
                  ? (<><FaSpinner className="tvp-spin" /> {t('ticketViewsParameters.submitting', 'Enregistrement…')}</>)
                  : editingView
                    ? t('common.save', 'Enregistrer')
                    : t('ticketViewsParameters.submit', 'Créer la vue')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketViewsParameters;
